import React, { useState, useEffect, useRef, useCallback } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { operatorCommissionsAPI, rechargeApiManagerAPI, commissionPackagesAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Percent, Plus, Trash2, Loader2, Save, Package, X, Users, Pencil } from 'lucide-react';

const OPERATORS = ['jio', 'airtel', 'vi', 'bsnl_topup', 'bsnl_special', 'jio_lite', 'mtnl', 'tatasky', 'dishtv', 'd2h', 'airtel_dth', 'sun_direct', 'google_play'];
const SERVICES = [{ value: 'prepaid', label: 'Prepaid' }, { value: 'dth', label: 'DTH' }, { value: 'postpaid', label: 'Postpaid' }, { value: 'google_play', label: 'Google Play' }];
const API_SLOTS = ['api_1', 'api_2', 'api_3', 'api_4', 'api_5'];
const USER_TYPES = [
  { value: 'retailer', label: 'Retailer' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'super_distributor', label: 'Super Distributor' },
];

// Inline editable number
const EditNum = ({ value, onSave, suffix = '%', color = 'text-green-700', testId }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value);
  const ref = useRef(null);
  useEffect(() => { setVal(value); }, [value]);
  useEffect(() => { if (editing && ref.current) ref.current.select(); }, [editing]);
  const save = () => { setEditing(false); const n = parseFloat(val) || 0; if (n !== value) onSave(n); };
  if (editing) return <input ref={ref} type="text" inputMode="decimal" value={val} onChange={e => setVal(e.target.value.replace(/[^0-9.]/g, ''))} onBlur={save} onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setVal(value); setEditing(false); }}} className="w-14 h-7 text-xs font-bold text-center border border-blue-400 rounded bg-blue-50 outline-none ring-2 ring-blue-200" data-testid={testId} />;
  return <span onClick={() => setEditing(true)} className={`font-bold cursor-pointer px-1.5 py-0.5 rounded hover:bg-slate-100 transition inline-block text-xs ${color}`} title="Click to edit" data-testid={testId}>{value}{suffix}</span>;
};

// Inline API select
const ApiSelect = ({ value, apis, onSave, testId }) => (
  <select value={value || ''} onChange={e => onSave(e.target.value || null)} className="h-7 text-[10px] border border-slate-200 rounded px-1 bg-white w-full min-w-[70px] cursor-pointer" data-testid={testId}>
    <option value="">-</option>
    {apis.map(a => <option key={a.id} value={a.id} disabled={a.status !== 'active'}>{a.name.split(' ')[0]}</option>)}
  </select>
);

const TypeSelect = ({ value, onSave }) => (
  <select value={value} onChange={e => onSave(e.target.value)} className="h-7 text-[10px] border border-slate-200 rounded px-1 bg-white cursor-pointer">
    <option value="percentage">%</option>
    <option value="flat">Flat</option>
  </select>
);

const OperatorCommissions = () => {
  const [items, setItems] = useState([]);
  const [apis, setApis] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserType, setSelectedUserType] = useState('retailer');
  const [selectedPkg, setSelectedPkg] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pkgDialogOpen, setPkgDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newPkgName, setNewPkgName] = useState('');
  const [editPkgId, setEditPkgId] = useState(null);
  const [editPkgName, setEditPkgName] = useState('');
  const [form, setForm] = useState({
    operator: '', service: 'prepaid', commission_type: 'percentage',
    retailer_commission: 0, distributor_commission: 0, super_distributor_commission: 0,
    api_1: '', api_2: '', api_3: '', api_4: '', api_5: '', package_id: '', status: 'active'
  });

  // Fetch APIs (one-time)
  const fetchApis = useCallback(async () => {
    try {
      const a = await rechargeApiManagerAPI.list();
      setApis(a.data || []);
    } catch {}
  }, []);

  // Fetch packages for selected user type
  const fetchPackages = useCallback(async (userType) => {
    try {
      const p = await commissionPackagesAPI.list({ user_type: userType || selectedUserType });
      setPackages(p.data || []);
    } catch {}
  }, [selectedUserType]);

  // Fetch commissions - filtered by package_id if selected
  const fetchItems = useCallback(async (pkgId) => {
    setLoading(true);
    try {
      const params = {};
      const pkg = pkgId !== undefined ? pkgId : selectedPkg;
      if (pkg) params.package_id = pkg;
      const r = await operatorCommissionsAPI.list(params);
      setItems(r.data || []);
    } catch {} finally { setLoading(false); }
  }, [selectedPkg]);

  useEffect(() => { fetchApis(); }, [fetchApis]);

  // When user type changes, fetch packages and clear package selection
  useEffect(() => {
    setSelectedPkg('');
    fetchPackages(selectedUserType);
  }, [selectedUserType]);

  // When package selection changes, fetch commissions
  useEffect(() => { fetchItems(); }, [selectedPkg]);

  const handleInlineSave = async (id, field, newVal) => {
    try {
      await operatorCommissionsAPI.update(id, { [field]: newVal });
      setItems(prev => prev.map(i => i.id === id ? { ...i, [field]: newVal } : i));
      toast.success('Saved');
    } catch { toast.error('Failed'); }
  };

  const handleAdd = async () => {
    if (!form.operator) { toast.error('Operator required'); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (selectedPkg) payload.package_id = selectedPkg;
      await operatorCommissionsAPI.create(payload);
      toast.success('Created'); fetchItems(); setDialogOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await operatorCommissionsAPI.delete(id); toast.success('Deleted'); fetchItems(); } catch { toast.error('Failed'); }
  };

  // Package management
  const handleAddPkg = async () => {
    if (!newPkgName.trim()) return;
    try {
      await commissionPackagesAPI.create({ name: newPkgName.trim(), user_type: selectedUserType });
      toast.success('Package created');
      setNewPkgName('');
      fetchPackages(selectedUserType);
    } catch { toast.error('Failed'); }
  };

  const handleUpdatePkg = async (id) => {
    if (!editPkgName.trim()) return;
    try {
      await commissionPackagesAPI.update(id, { name: editPkgName.trim() });
      toast.success('Package updated');
      setEditPkgId(null);
      setEditPkgName('');
      fetchPackages(selectedUserType);
    } catch { toast.error('Failed'); }
  };

  const handleDeletePkg = async (id) => {
    if (!window.confirm('Delete this package and all its commissions?')) return;
    try {
      await commissionPackagesAPI.delete(id);
      toast.success('Package deleted');
      if (selectedPkg === id) setSelectedPkg('');
      fetchPackages(selectedUserType);
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  const getSuffix = (type) => type === 'percentage' ? '%' : '';

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="operator-commissions-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2"><Percent className="w-5 h-5" style={{ color: '#4680ff' }} />Set Commission</h1>
          <Button onClick={() => setDialogOpen(true)} className="bg-[#4680ff] hover:bg-[#2563eb] text-white h-9 px-4 text-sm" data-testid="add-opcomm-btn"><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>

        {/* Top Filters: User Type → Package → Manage */}
        <Card><CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            {/* User Type Select */}
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              <Select value={selectedUserType} onValueChange={v => setSelectedUserType(v)}>
                <SelectTrigger className="w-[180px] h-9" data-testid="filter-user-type"><SelectValue placeholder="User Type" /></SelectTrigger>
                <SelectContent>
                  {USER_TYPES.map(ut => <SelectItem key={ut.value} value={ut.value}>{ut.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Package Select */}
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-slate-500" />
              <Select value={selectedPkg || 'all'} onValueChange={v => setSelectedPkg(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[200px] h-9" data-testid="filter-package"><SelectValue placeholder="All Packages" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Packages</SelectItem>
                  {packages.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Package management button */}
            <Button variant="outline" size="sm" className="h-9 text-xs" onClick={() => setPkgDialogOpen(true)} data-testid="manage-packages-btn">
              <Package className="w-3 h-3 mr-1" /> Manage Packages
            </Button>

            {/* Show selected package badge */}
            {selectedPkg && (
              <Badge className="bg-blue-100 text-blue-800 text-xs">
                {packages.find(p => p.id === selectedPkg)?.name || 'Selected'}
                <button onClick={() => setSelectedPkg('')} className="ml-1"><X className="w-3 h-3" /></button>
              </Badge>
            )}
          </div>
        </CardContent></Card>

        {/* Commission Table */}
        <Card><CardContent className="p-0">
          {loading ? <div className="p-6 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
            : items.length === 0 ? <div className="text-center py-10 text-muted-foreground"><p className="text-sm">No commissions configured{selectedPkg ? ' for this package' : ''}</p></div>
            : <div className="overflow-x-auto"><Table>
              <TableHeader><TableRow className="text-[10px]">
                <TableHead className="text-[10px] px-2">Operator</TableHead>
                <TableHead className="text-[10px] px-2">Service</TableHead>
                <TableHead className="text-[10px] px-1 text-center">API 1</TableHead>
                <TableHead className="text-[10px] px-1 text-center">API 2</TableHead>
                <TableHead className="text-[10px] px-1 text-center">API 3</TableHead>
                <TableHead className="text-[10px] px-1 text-center">API 4</TableHead>
                <TableHead className="text-[10px] px-1 text-center">API 5</TableHead>
                <TableHead className="text-[10px] px-2 text-center">Type</TableHead>
                <TableHead className="text-[10px] px-2 text-center">Retailer</TableHead>
                <TableHead className="text-[10px] px-2 text-center">Distributor</TableHead>
                <TableHead className="text-[10px] px-2 text-center">Super Dist.</TableHead>
                <TableHead className="text-[10px] px-2 text-center">Status</TableHead>
                <TableHead className="text-[10px] px-2"></TableHead>
              </TableRow></TableHeader>
              <TableBody>{items.map(i => (
                <TableRow key={i.id} className="text-xs">
                  <TableCell className="px-2 font-semibold capitalize text-xs">{i.operator?.replace(/_/g, ' ')}</TableCell>
                  <TableCell className="px-2"><Badge variant="outline" className="capitalize text-[10px]">{i.service}</Badge></TableCell>
                  {API_SLOTS.map(slot => (
                    <TableCell key={slot} className="px-1">
                      <ApiSelect value={i[slot]} apis={apis} onSave={v => handleInlineSave(i.id, slot, v)} testId={`${slot}-${i.id}`} />
                    </TableCell>
                  ))}
                  <TableCell className="px-2 text-center">
                    <TypeSelect value={i.commission_type} onSave={v => handleInlineSave(i.id, 'commission_type', v)} />
                  </TableCell>
                  <TableCell className="px-2 text-center">
                    <EditNum value={i.retailer_commission} suffix={getSuffix(i.commission_type)} color="text-green-700" onSave={v => handleInlineSave(i.id, 'retailer_commission', v)} testId={`ret-${i.id}`} />
                  </TableCell>
                  <TableCell className="px-2 text-center">
                    <EditNum value={i.distributor_commission} suffix={getSuffix(i.commission_type)} color="text-blue-700" onSave={v => handleInlineSave(i.id, 'distributor_commission', v)} testId={`dist-${i.id}`} />
                  </TableCell>
                  <TableCell className="px-2 text-center">
                    <EditNum value={i.super_distributor_commission} suffix={getSuffix(i.commission_type)} color="text-purple-700" onSave={v => handleInlineSave(i.id, 'super_distributor_commission', v)} testId={`sd-${i.id}`} />
                  </TableCell>
                  <TableCell className="px-2 text-center">
                    <button onClick={() => handleInlineSave(i.id, 'status', i.status === 'active' ? 'inactive' : 'active')}>
                      <Badge className={`text-[9px] cursor-pointer ${i.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>{i.status}</Badge>
                    </button>
                  </TableCell>
                  <TableCell className="px-2">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-500" onClick={() => handleDelete(i.id)}><Trash2 className="w-3 h-3" /></Button>
                  </TableCell>
                </TableRow>
              ))}</TableBody>
            </Table></div>}
        </CardContent></Card>

        {/* Add Commission Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogContent className="max-w-lg" data-testid="opcomm-dialog" aria-describedby="opcomm-dialog-desc"><DialogHeader><DialogTitle>Add Operator Commission</DialogTitle><p id="opcomm-dialog-desc" className="text-xs text-muted-foreground">Configure commission rates and API routing for an operator.</p></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-[10px]">Operator *</Label>
                <Select value={form.operator} onValueChange={v => setForm({ ...form, operator: v })}><SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{OPERATORS.map(o => <SelectItem key={o} value={o} className="capitalize">{o.replace('_', ' ')}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1"><Label className="text-[10px]">Service</Label>
                <Select value={form.service} onValueChange={v => setForm({ ...form, service: v })}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{SERVICES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1"><Label className="text-[10px]">Type</Label>
                <Select value={form.commission_type} onValueChange={v => setForm({ ...form, commission_type: v })}><SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="percentage">%</SelectItem><SelectItem value="flat">Flat</SelectItem></SelectContent></Select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1"><Label className="text-[10px]">Retailer</Label><Input type="number" step="0.01" min="0" value={form.retailer_commission} onChange={e => setForm({ ...form, retailer_commission: e.target.value })} className="h-9" /></div>
              <div className="space-y-1"><Label className="text-[10px]">Distributor</Label><Input type="number" step="0.01" min="0" value={form.distributor_commission} onChange={e => setForm({ ...form, distributor_commission: e.target.value })} className="h-9" /></div>
              <div className="space-y-1"><Label className="text-[10px]">Super Dist.</Label><Input type="number" step="0.01" min="0" value={form.super_distributor_commission} onChange={e => setForm({ ...form, super_distributor_commission: e.target.value })} className="h-9" /></div>
            </div>
            <div className="space-y-1"><Label className="text-[10px]">Package</Label>
              <Select value={form.package_id || 'none'} onValueChange={v => setForm({ ...form, package_id: v === 'none' ? '' : v })}><SelectTrigger className="h-9"><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent><SelectItem value="none">Default</SelectItem>{packages.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button onClick={handleAdd} disabled={saving} className="bg-[#4680ff] hover:bg-[#2563eb] text-white">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Add</Button></DialogFooter>
        </DialogContent></Dialog>

        {/* Manage Packages Dialog */}
        <Dialog open={pkgDialogOpen} onOpenChange={setPkgDialogOpen}><DialogContent className="max-w-md" data-testid="pkg-dialog" aria-describedby="pkg-dialog-desc"><DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="w-5 h-5" />Manage Packages - {USER_TYPES.find(ut => ut.value === selectedUserType)?.label}</DialogTitle><p id="pkg-dialog-desc" className="text-xs text-muted-foreground">Create and manage commission packages for {USER_TYPES.find(ut => ut.value === selectedUserType)?.label}s.</p></DialogHeader>
          <div className="space-y-3 py-2">
            {/* Add new package */}
            <div className="flex gap-2">
              <Input value={newPkgName} onChange={e => setNewPkgName(e.target.value)} placeholder="New package name" className="h-9 text-sm" onKeyDown={e => e.key === 'Enter' && handleAddPkg()} data-testid="new-pkg-input" />
              <Button onClick={handleAddPkg} className="h-9 bg-[#4680ff] hover:bg-[#2563eb] text-white px-3" data-testid="add-pkg-btn"><Plus className="w-4 h-4" /></Button>
            </div>

            {/* List packages */}
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {packages.length === 0 ? <p className="text-sm text-slate-400 text-center py-4">No packages for {USER_TYPES.find(ut => ut.value === selectedUserType)?.label}</p> : packages.map(p => (
                <div key={p.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg border">
                  {editPkgId === p.id ? (
                    <div className="flex gap-2 flex-1 mr-2">
                      <Input value={editPkgName} onChange={e => setEditPkgName(e.target.value)} className="h-8 text-sm" onKeyDown={e => { if (e.key === 'Enter') handleUpdatePkg(p.id); if (e.key === 'Escape') setEditPkgId(null); }} autoFocus data-testid={`edit-pkg-input-${p.id}`} />
                      <Button size="sm" className="h-8 px-2 bg-green-600 hover:bg-green-700 text-white" onClick={() => handleUpdatePkg(p.id)}><Save className="w-3 h-3" /></Button>
                      <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setEditPkgId(null)}><X className="w-3 h-3" /></Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold">{p.name}</span>
                      <Badge variant="outline" className={`text-[9px] ${p.status === 'active' ? 'border-green-300 text-green-700' : 'border-red-300 text-red-700'}`}>{p.status}</Badge>
                    </div>
                  )}
                  {editPkgId !== p.id && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-blue-500 hover:bg-blue-50" onClick={() => { setEditPkgId(p.id); setEditPkgName(p.name); }} data-testid={`edit-pkg-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-50" onClick={() => handleDeletePkg(p.id)} data-testid={`del-pkg-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </DialogContent></Dialog>
      </div>
    </DashboardLayout>
  );
};
export default OperatorCommissions;
